import type { HealthcareService } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react-hooks";
import { HEALTHCARE_SERVICE_ENTITY_ID_SYSTEM } from "@repo/api";
import type { PatientDto } from "@repo/data-store";
import { bookConsultation, usePatients } from "@repo/data-store";
import { asHealthcareServiceId, CONSULTATION_TYPE_SYSTEM, type HealthcareServiceId } from "@repo/domain";
import { format, parseISO } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { useFindConsultationCandidates } from "@/features/scheduling/hooks/use-find-bookable-candidates";
import type { TimeSlot } from "@/features/scheduling/lib/appointment-utils";
import { useAppForm } from "@/shared/forms/app-form";
import { useSubmitOnce } from "@/shared/forms/submit-once";

export const CONSULTATION_DURATION_MINUTES = 60;
export const CONSULTATION_TYPES = [
  { code: "initial-consultation", label: "Initial consultation" },
  { code: "report-of-findings", label: "Report of findings" },
] as const;

export type ConsultationTypeCode = (typeof CONSULTATION_TYPES)[number]["code"];

/**
 * NO LOCATION FIELD. An in-person consult needs *a* treatment room, not a
 * particular one — the server resolves every active room as the eligible set and
 * availability picks which one, exactly as it does for a protocol step.
 *
 * Asking the user to name the room made them guess at availability: pick the
 * wrong room and the whole day came back empty, with nothing saying why.
 */
export const consultBookingFormSchema = z.object({
  consultationType: z.enum(["initial-consultation", "report-of-findings"]),
  practitionerId: z.string().min(1, "Practitioner is required"),
  patientId: z.string().min(1, "Patient is required"),
  date: z.date(),
  startTime: z.string().min(1, "Valid time required"),
  endTime: z.string().min(1, "Valid time required"),
  modality: z.enum(["virtual", "in-person"]),
});

export type ConsultBookingFormValues = z.infer<typeof consultBookingFormSchema>;

export const DEFAULT_CONSULT_BOOKING_FORM_VALUES: ConsultBookingFormValues = {
  consultationType: "initial-consultation",
  practitionerId: "",
  patientId: "",
  date: new Date(),
  startTime: "",
  endTime: "",
  modality: "virtual",
};

export interface UseConsultBookingFormOptions {
  initialPatientId?: string;
  onSuccess?: () => void;
}

/** Maps consultation type code → stable HealthcareService domain identity. */
export type ConsultHsMap = Record<string, HealthcareServiceId>;

function healthcareServiceDomainId(service: HealthcareService): HealthcareServiceId | undefined {
  const value = service.identifier?.find(
    (identifier) => identifier.system === HEALTHCARE_SERVICE_ENTITY_ID_SYSTEM,
  )?.value;
  return value ? asHealthcareServiceId(value) : undefined;
}

export interface UseConsultBookingFormReturn {
  patients: PatientDto[];
  isLoadingPatients: boolean;
  hsMap: ConsultHsMap;
  isLoadingHs: boolean;
  isPending: boolean;
  defaultValues: ConsultBookingFormValues;
  validators: { onDynamic: typeof consultBookingFormSchema };
  handleSubmit: (value: ConsultBookingFormValues) => Promise<void>;
  /** One slot per candidate — a candidate IS a bookable start, already on the grid. */
  slots: TimeSlot[];
  isLoadingAvailability: boolean;
  availabilityError: string | null;
  queryCandidates: ReturnType<typeof useFindConsultationCandidates>["query"];
  resetCandidates: ReturnType<typeof useFindConsultationCandidates>["reset"];
}

export function useConsultBookingFormData(options?: UseConsultBookingFormOptions): UseConsultBookingFormReturn {
  const { initialPatientId, onSuccess } = options ?? {};
  const medplum = useMedplum();

  const { patients, isLoading: isLoadingPatients } = usePatients();

  // Resolve the consultation HealthcareService ID.
  const [hsMap, setHsMap] = useState<ConsultHsMap>({});
  const [isLoadingHs, setIsLoadingHs] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingHs(true);
    medplum
      .searchResources("HealthcareService", { "service-type": `${CONSULTATION_TYPE_SYSTEM}|` })
      .then((resources: HealthcareService[]) => {
        if (cancelled) return;
        const map: ConsultHsMap = {};
        for (const hs of resources) {
          const serviceId = healthcareServiceDomainId(hs);
          if (!serviceId) continue;
          const coding = hs.type?.[0]?.coding ?? [];
          for (const c of coding) {
            if (c.system === CONSULTATION_TYPE_SYSTEM && c.code) {
              map[c.code] = serviceId;
            }
          }
        }
        setHsMap(map);
        setIsLoadingHs(false);
      })
      .catch(() => {
        if (!cancelled) setIsLoadingHs(false);
      });
    return () => {
      cancelled = true;
    };
  }, [medplum]);

  const [isPending, setIsPending] = useState(false);
  const { once } = useSubmitOnce();
  const {
    candidates,
    isLoading: isLoadingAvailability,
    error: availabilityError,
    query: queryCandidates,
    reset: resetCandidates,
  } = useFindConsultationCandidates();

  const defaultValues: ConsultBookingFormValues = useMemo(
    () => ({
      ...DEFAULT_CONSULT_BOOKING_FORM_VALUES,
      patientId: initialPatientId ?? "",
    }),
    [initialPatientId],
  );

  const slots = useMemo(
    () =>
      candidates.map((candidate): TimeSlot => {
        const start = parseISO(candidate.start);
        const end = parseISO(candidate.end);
        return {
          startTime: candidate.start,
          endTime: candidate.end,
          dateDisplay: format(start, "EEE, MMM d"),
          display: format(start, "h:mm a"),
          endDisplay: format(end, "h:mm a"),
        };
      }),
    [candidates],
  );

  const handleSubmit = useCallback(
    async (value: ConsultBookingFormValues) => {
      const healthcareServiceId = hsMap[value.consultationType];
      if (!healthcareServiceId) {
        toast.error("Consultation service not available — please try again.");
        return;
      }
      const candidate = candidates.find((c) => c.start === value.startTime);
      if (!candidate) {
        toast.error("Selected slot is no longer available — please pick another time.");
        return;
      }
      setIsPending(true);
      try {
        await once(async (id) => {
          await bookConsultation({
            id,
            candidate,
            healthcareServiceId,
            patientId: value.patientId,
            // Declared, not inferred from whether a room got seated.
            modality: value.modality,
          }).isPersisted.promise;
          onSuccess?.();
        });
      } catch {
      } finally {
        setIsPending(false);
      }
    },
    [hsMap, onSuccess, candidates, once],
  );

  return {
    patients: patients ?? [],
    isLoadingPatients,
    hsMap,
    isLoadingHs,
    isPending,
    defaultValues,
    validators: { onDynamic: consultBookingFormSchema },
    handleSubmit,
    slots,
    isLoadingAvailability,
    availabilityError,
    queryCandidates,
    resetCandidates,
  };
}

export { useAppForm };
